// frontend/src/utils/validate-project-data.ts
import i18n from "@/i18n";
import { ProjectFieldMetadata } from "@/types/ProjectFieldMetadata";

export interface ValidationError {
  field: string;
  message: string;
}

export function validateProjectData(
  fields: Record<string, any>,
  metadata: ProjectFieldMetadata[]
): { valid: boolean; errors: ValidationError[] } {
  const errors: ValidationError[] = [];

  for (const [name, value] of Object.entries(fields)) {
    const meta = metadata.find(m => m.field_name === name);
    if (!meta) continue; // Ignore fields without metadata

    const label = meta.original_label || name;

    // Required check
    if (meta.required && (value === null || value === "")) {
      errors.push({ field: name, message: i18n.t("project.validation.required", { label }) });
      continue; // Skip further checks for this field
    }

    // Type check (basic)
    if (meta.type === "number" && value !== null && value !== "" && isNaN(Number(value))) {
      errors.push({ field: name, message: i18n.t("project.validation.must_be_number", { label }) });
    }

    // Min value check
    if (
      meta.min_value != null &&
      value !== null &&
      value !== "" &&
      !isNaN(Number(value)) &&
      Number(value) < meta.min_value
    ) {
      errors.push({ field: name, message: i18n.t("project.validation.min", { label, min: meta.min_value }) });
    }

    // Max value check
    if (
      meta.max_value != null &&
      value !== null &&
      value !== "" &&
      !isNaN(Number(value)) &&
      Number(value) > meta.max_value
    ) {
      errors.push({ field: name, message: i18n.t("project.validation.max", { label, max: meta.max_value }) });
    }

    // Pattern check
    if (meta.pattern && value) {
      try {
        if (!new RegExp(meta.pattern).test(String(value))) {
          errors.push({ field: name, message: i18n.t("project.validation.invalid_format", { label }) });
        }
      } catch {
        console.warn(`Invalid regex pattern for field ${name}: ${meta.pattern}`);
      }
    }


    // Allowed values check
    if (meta.allowed_values && meta.allowed_values.length > 0 && value && !meta.allowed_values.includes(value)) {
      errors.push({
        field: name,
        message: i18n.t("project.validation.one_of", { label, values: meta.allowed_values.join(", ") })
      });
    }
  }

  return { valid: errors.length === 0, errors };
}
